import React, {useState} from 'react'
import {highlight, languages} from 'prismjs/components/prism-core'
import 'prismjs/components/prism-clike'
import 'prismjs/components/prism-javascript'
import 'prismjs/components/prism-sql'

import CodeEditor from 'react-simple-code-editor'
import {COLORS, THEME_COLORS} from 'ac_ui'
import {LS_EDITOR_CURRENT_QUERY} from '../constant'
import {QeSqlResultDto} from 'learndb-share'
import {IReduxState} from '../../../redux/types/IReduxState'
import {useDispatch, useSelector} from 'react-redux'
import {taskActions} from '../redux/actions'
import {setStorageData} from '../../../../storage/methods'
import {isEmpty} from 'ac-share'
import {TaskSolvedControls} from '../components/TaskSolvedControls'

const EDITOR_TEXTAREA_ID = 'EDITOR_TEXTAREA_ID'
const SQL_ICON_PREDIX = 'EDITOR_SQL_'

function getSqlTagId(sqlId: string): string {
  return SQL_ICON_PREDIX + sqlId
}

function getErrorIcon(sqlId: string): string {
  return `<svg id="${getSqlTagId(sqlId)}" style="width:18px;height:18px; margin-right: 4px" viewBox="0 0 24 24" >
            <path fill="${
              THEME_COLORS.danger
            }" d="M12,20C7.59,20 4,16.41 4,12C4,7.59 7.59,4 12,4C16.41,4 20,7.59 20,12C20,16.41 16.41,20 12,20M12,2C6.47,2 2,6.47 2,12C2,17.53 6.47,22 12,22C17.53,22 22,17.53 22,12C22,6.47 17.53,2 12,2M14.59,8L12,10.59L9.41,8L8,9.41L10.59,12L8,14.59L9.41,16L12,13.41L14.59,16L16,14.59L13.41,12L16,9.41L14.59,8Z" />
          </svg>`
}

function getSuccessIcon(sqlId: string): string {
  return `<svg id="${getSqlTagId(sqlId)}" style="width:18px;height:18px; margin-right: 4px" viewBox="0 0 24 24" >
            <path fill="${
              THEME_COLORS.success
            }" d="M12 2C6.5 2 2 6.5 2 12S6.5 22 12 22 22 17.5 22 12 17.5 2 12 2M12 20C7.59 20 4 16.41 4 12S7.59 4 12 4 20 7.59 20 12 16.41 20 12 20M16.59 7.58L10 14.17L7.41 11.59L6 13L10 17L18 9L16.59 7.58Z" />
          </svg>`
}

interface IEditorProps {
  id: string
}

const Editor: React.FC<IEditorProps> = () => {
  const taskDispatch = useDispatch()
  const query = useSelector<IReduxState, string | undefined>((state) => state.task?.query)
  const isQueryResultEquals = useSelector<IReduxState, boolean | undefined>((state) => state.task?.isQueryResultEquals)
  const sqlResults = useSelector<IReduxState, QeSqlResultDto[] | undefined>((state) => state.task?.sqlResults)

  const [selectedLine, setSelectedLine] = useState<number>(undefined)

  const highlightCode = (code: string): string => {
    const sqlLineMap: {
      [lineNumber: number]: {
        sqlId: string
        error?: string
        isSuccess?: boolean
      }
    } = {}
    sqlResults?.forEach((res) => {
      const lineNumber = res.query.lineStart
      if (!sqlLineMap[lineNumber]) {
        sqlLineMap[lineNumber] = {
          sqlId: res.query.id,
        }
      }
      if (res.result.error) {
        sqlLineMap[lineNumber].sqlId = res.query.id
        sqlLineMap[lineNumber].error = res.result.error
      } else {
        sqlLineMap[lineNumber].isSuccess
      }
    })

    return highlight(code, languages.sql)
      .split('\n')
      .map((line: string, i: number) => {
        const lineNumber = i + 1
        const isSelected = lineNumber === selectedLine
        let icon: string
        if (sqlLineMap[lineNumber]) {
          const {sqlId, error} = sqlLineMap[lineNumber]
          if (error) {
            icon = getErrorIcon(sqlId)
          } else {
            icon = getSuccessIcon(sqlId)
          }
        }

        return `<span class="editorLineNumber">${icon || ''}${lineNumber}</span><span class="${
          isSelected ? 'selected-span' : 'hidden-span'
        }"></span>${line}`
      })
      .join('\n')
  }

  const updateSelectedLine = (el?: any, isBlur?: boolean): void => {
    if (isBlur) {
      setSelectedLine(undefined)
    } else {
      const element: any = el || document.getElementById(EDITOR_TEXTAREA_ID)
      if (element) {
        let position = undefined
        let value = undefined
        if (!isEmpty(element.selectionStart)) {
          position = element.selectionStart
          value = element.value
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          //@ts-ignore
        } else if (document.selection) {
          element.focus()

          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          //@ts-ignore
          const r = document.selection.createRange()
          if (r == null) {
            position = 0
            value = ''
          } else {
            const re = element.createTextRange()
            const rc = re.duplicate()
            re.moveToBookmark(r.getBookmark())
            rc.setEndPoint('EndToStart', re)

            position = rc.text.length
            value = rc.text
          }
        }
        if (value !== undefined) {
          const str = value.substring(0, position)
          const line = str.split('\n').length
          setSelectedLine(line)
        }
      }
    }
  }

  return (
    <div className={'task-bottom-collapsible-block-body h-100'}>
      <div className={'task-tab-content custom-scroll pt-x2 pr-x1 pl-x2 mr-x1'}>
        <div className={'code-editor'}>
          <div>
            <CodeEditor
              value={query}
              onValueChange={(value) => {
                taskDispatch(taskActions.setState({query: value}))
                if (!isQueryResultEquals) {
                  setStorageData(LS_EDITOR_CURRENT_QUERY, value, true)
                }
              }}
              highlight={() => highlightCode(query)}
              padding={0}
              onKeyUp={() => {
                updateSelectedLine()
              }}
              onClick={() => {
                updateSelectedLine()
              }}
              onFocus={(event) => {
                updateSelectedLine(event.target)
              }}
              onBlur={() => {
                updateSelectedLine(null, true)
              }}
              //eslint-disable-next-line react-native/no-inline-styles
              style={{
                fontFamily: 'Menlo, Monaco, "Courier New", monospace',
                fontSize: '14px',
                lineHeight: '22px',
                height: '100%',
                outline: 'none',
                backgroundColor: COLORS.white,
              }}
              textareaClassName={'code-editor-textarea'}
              textareaId={EDITOR_TEXTAREA_ID}
              autoFocus
              insertSpaces
              placeholder={'SELECT ...'}
              onKeyDown={(evt) => {
                if ((evt.key === 'Enter' && evt.metaKey) || (evt.key === 'Enter' && evt.ctrlKey)) {
                  evt.preventDefault()
                }
              }}
            />
          </div>
          <div
            className={'editor-placeholder-wrapper flex-grow-1'}
            onClick={() => {
              const editor = document.getElementById(EDITOR_TEXTAREA_ID)
              if (editor) {
                editor.focus()
              }
            }}>
            <div className={'editor-placeholder'} />
          </div>
        </div>
      </div>
      {isQueryResultEquals ? <TaskSolvedControls /> : null}
    </div>
  )
}

export {Editor}
